<template>
    <div class="section-type-editor">
      <button
        class="section-btn"
        title="Change section type"
        @click="showSectionDialog"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
        <span class="section-indicator">{{ getSectionLabel(currentSection) }}</span>
      </button>

      <!-- Section Edit Dialog -->
      <v-dialog
        v-model="showSectionEditDialog"
        max-width="400px"
        persistent
        content-class="section-dialog"
      >
        <v-card>
          <v-card-title class="dialog-title">
            Edit Section Type
            <v-spacer></v-spacer>
            <v-btn icon @click="closeSectionDialog" class="close-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="editedSection"
                    :items="sectionTypes"
                    item-text="label"
                    item-value="value"
                    label="Section Type"
                    outlined
                    dense
                    return-object
                  >
                    <template v-slot:selection="{ item }">
                      <span>{{ item.icon }} {{ item.label }}</span>
                    </template>
                    <template v-slot:item="{ item }">
                      <span>{{ item.icon }} {{ item.label }}</span>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="grey darken-1"
              @click="closeSectionDialog"
              :disabled="isSubmitting"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              @click="updateSectionType"
              :disabled="isSubmitting"
              :loading="isSubmitting"
            >
              <span class="black--text">Update Section</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>

  <script>
  import API from '@/api';
  import { mapActions } from 'vuex';

  export default {
    name: 'SectionTypeEditor',
    props: {
      messageId: {
        type: Number,
        required: true
      },
      segmentIndex: {
        type: Number,
        required: true
      },
      currentSection: {
        type: String,
        default: 'custom'
      }
    },
    data() {
      return {
        showSectionEditDialog: false,
        editedSection: null,
        isSubmitting: false,
        sectionTypes: [
          { value: 'preview', label: 'Preview', icon: '🔍' },
          { value: 'intro', label: 'Intro', icon: '🎙️' },
          { value: 'main', label: 'Main Content', icon: '📝' },
          { value: 'break', label: 'Sponsor Break', icon: '💰' },
          { value: 'last', label: 'Closing', icon: '🏁' },
          { value: 'discussion', label: 'Discussion', icon: '💬' },
          { value: 'insight', label: 'Insight', icon: '💡' },
          { value: 'resonance', label: 'Resonance', icon: '🔄' },
          { value: 'evolution', label: 'Evolution', icon: '🌱' },
          { value: 'portal', label: 'Portal', icon: '🚪' },
          { value: 'custom', label: 'Custom', icon: '🛠️' }
        ]
      };
    },
    methods: {
      ...mapActions('websocket', ['showTempMessageFn']),

      showSectionDialog() {
        // Find the section object that matches the current section
        const currentSectionObj = this.sectionTypes.find(s => s.value === this.currentSection);
        this.editedSection = currentSectionObj || this.sectionTypes.find(s => s.value === 'custom');
        this.showSectionEditDialog = true;
      },

      closeSectionDialog() {
        if (!this.isSubmitting) {
          this.showSectionEditDialog = false;
        }
      },

      getSectionLabel(sectionValue) {
        const section = this.sectionTypes.find(s => s.value === sectionValue);
        return section ? `${section.icon} ${section.label}` : '🛠️ Custom';
      },

      async updateSectionType() {
        if (this.isSectionSubmitting) return;

        this.isSubmitting = true;

        try {
          const payload = {
            section: this.editedSection.value
          };

          const response = await API.patch(
            `/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}/section`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data.success) {
            // Emit section updated event with complete data
            this.$emit('section-updated', {
              index: this.segmentIndex,
              section: this.editedSection.value,
              updatedResponse: response.data.updated_response
            });

            this.showTempMessageFn({
              message: 'Section type updated successfully',
              type: 'success',
              timeout: 3000
            });

            // Reset submitting state before closing
            this.isSubmitting = false;

            // Use nextTick to ensure state is updated
            this.$nextTick(() => {
              this.showSectionEditDialog = false;
            });
          }
        } catch (error) {
          console.error('Error updating section type:', error);
          this.showTempMessageFn({
            message: 'Failed to update section type. Please try again.',
            type: 'error',
            timeout: 5000
          });
        } finally {
          this.isSubmitting = false;
        }
      }
    }
  };
  </script>

  <style scoped>
  .section-type-editor {
    display: inline-block;
  }

  .section-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    color: #9c27b0; /* Purple for section indicator */
    transition: background-color 0.2s, color 0.2s;
  }

  .section-btn:hover {
    background-color: rgba(156, 39, 176, 0.1);
  }

  .section-indicator {
    font-size: 12px;
    margin-left: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
  }

  /* Custom dialog styling */
  .section-dialog {
    z-index: 1000 !important;
  }

  .dialog-title {
    position: relative;
    padding-bottom: 16px;
    font-size: 18px;
    font-weight: 500;
  }

  .close-btn {
    position: absolute;
    top: 0;
    right: 0;
  }

  /* Override Vuetify styles if needed */
  :deep(.v-text-field__details),
  :deep(.v-messages) {
    min-height: 18px !important;
  }

  :deep(.v-input__slot) {
    margin-bottom: 0 !important;
  }
  </style>
